import { Button, Flex, Group, InputWrapper, Pill, Text } from '@mantine/core';
import { useController } from 'react-hook-form';
import type { FieldValues, UseControllerProps } from 'react-hook-form';

import { useI18n } from '../../hooks/useI18n.ts';
import { toStringArray } from '../../lib/toStringArray.ts';
import { parseError } from '../reactHookFormMantine/lib/parseError.ts';

type TagsSelectProps<T extends FieldValues> = UseControllerProps<T> & {
  data: { label: string; value: string }[];
  maxValues?: number;
  label: string;
};

export function TagsSelect<T extends FieldValues>({
  name,
  control,
  defaultValue,
  rules,
  shouldUnregister,
  data,
  maxValues,
  label,
  ...props
}: TagsSelectProps<T>) {
  const {
    field: { value: fieldValue, onChange },
    fieldState,
  } = useController<T>({
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
  });
  const value = toStringArray(fieldValue);
  const { t } = useI18n();

  const numberOfOptionsChosen = value.length;
  const optionsLeft = maxValues ? `${numberOfOptionsChosen} / ${maxValues}` : null;

  return (
    <InputWrapper {...props} error={parseError(label, fieldState.error)} label={label}>
      <Group component="ul" gap="xxxs" m={0} p={0} style={{ listStyle: 'none' }}>
        {data.map((item) => {
          const active = value.includes(item.value);
          const disabled = maxValues ? numberOfOptionsChosen === maxValues && !active : false;

          return (
            <li key={item.value}>
              <Button
                color="black"
                disabled={disabled}
                radius="xl"
                size="sm"
                styles={{ label: { fontSize: 15, fontWeight: 500 } }}
                variant={active ? 'filled' : 'outline'}
                onClick={() =>
                  onChange(active ? value.filter((valueItem) => valueItem !== item.value) : [...value, item.value])
                }
              >
                {item.label}
              </Button>
            </li>
          );
        })}
      </Group>
      {optionsLeft && (
        <Flex align="center" gap="xxxs" justify="flex-end" mt="md" w="100%">
          <Text size="xs">{t('event-form_tags-chosen')} </Text> <Pill>{optionsLeft}</Pill>
        </Flex>
      )}
    </InputWrapper>
  );
}
